/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useRouter} from 'next/compat/router';
import authMgr from '@ion/applications/src/authentication/AuthenticationManager';

export type useAuthLoginProps = {
  isAuthenticated: boolean
  onLogin: Function,
  onLoginV2: Function
}

/**
 * @hook useAuthLogin
 *
 * Provides a utility hook for performing login from the root layout component. 
 * Invoked when directly jumping into a deep link to ensure user is logged in prior
 * to routing to the deep link.
 *
 * ```typescript
 *
 *   import {useAuthLogin} from '@ion/applications/src/pages/login/useAuthLogin';
 *
 *   const SomeComponent = () => {
 *     // react component mount changes ...
 *     useAuthLogin({
 *       onLogin,
 *       onLoginV2,
 *       isAuthenticated
 *     });
 *   }
 *
 * ```
 */
export const useAuthLogin = ({onLogin, onLoginV2, isAuthenticated}: useAuthLoginProps): void => {
  const route    = useRouter();
  const dispatch = useDispatch();
  useEffect(() => {
    if (!isAuthenticated) {
      const action = authMgr.getIsOIDCEnabled() ? onLoginV2 : onLogin;
      dispatch(action(null, null, route) as any);
    }
  }, [isAuthenticated]);
};
